import React from "react";
import { motion } from "framer-motion";
import { TypeAnimation } from "react-type-animation";
import { FaReact, FaJava } from "react-icons/fa";
import { SiSpringboot } from "react-icons/si";
import { HiOutlineMail, HiOutlinePhone, HiOutlineLocationMarker } from "react-icons/hi";
import { personal } from "../data/content";
import { useLanguage } from "../context/LanguageContext";
import omarPhoto from "../omar-photo.jpg";

export default function Hero({ cvHref }) {
    const { t } = useLanguage();
    const sequence = t.hero.roles.flatMap((role) => [role, 1400]);

    return (
        <section id="home" className="hero">
            <div className="hero__glow hero__glow--one" aria-hidden="true" />
            <div className="hero__glow hero__glow--two" aria-hidden="true" />
            <div className="hero__grid" aria-hidden="true" />

            <div className="hero__inner">
                <motion.div
                    className="hero__content"
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: [0.22, 0.9, 0.32, 1] }}
                >
                    <span className="hero__eyebrow">{t.hero.eyebrow}</span>
                    <h1 className="hero__name">{personal.name}</h1>
                    <h2 className="hero__role">
                        <TypeAnimation
                            key={t.meta.code}
                            sequence={sequence}
                            wrapper="span"
                            speed={50}
                            cursor
                            repeat={Infinity}
                        />
                    </h2>
        

                    <div className="hero__actions">
                        <a
                            className="btn btn--primary"
                            href="#projects"
                            onClick={(e) => {
                                e.preventDefault();
                                document.getElementById("projects")?.scrollIntoView({ behavior: "smooth" });
                            }}
                        >
                            {t.hero.viewProjects}
                        </a>
                        <a className="btn btn--ghost" href={cvHref} target="_blank" rel="noreferrer">
                            {t.hero.downloadCV}
                        </a>
                    </div>

                    <ul className="hero__contact-list">
                        <li>
                            <a href={`mailto:${personal.email}`}>
                                <HiOutlineMail /> {personal.email}
                            </a>
                        </li>
                        <li>
                            <a href={`tel:${personal.phone}`}>
                                <HiOutlinePhone /> {personal.phone}
                            </a>
                        </li>
                        <li>
                            <span>
                                <HiOutlineLocationMarker /> {t.about.location}
                            </span>
                        </li>
                    </ul>
                </motion.div>

                <motion.div
                    className="hero__visual"
                    initial={{ opacity: 0, scale: 0.92 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 0.9, 0.32, 1] }}
                >
                    <div className="flip-card">
                        <motion.div
                            className="flip-card__inner"
                            animate={{ rotateY: [0, 0, 180, 180, 360] }}
                            transition={{
                                duration: 6,
                                times: [0, 0.45, 0.5, 0.95, 1],
                                repeat: Infinity,
                                ease: "easeInOut",
                            }}
                        >
                            <div className="flip-card__face flip-card__face--front">
                                <div className="code-card">
                                    <div className="code-card__bar">
                                        <span className="dot dot--red" />
                                        <span className="dot dot--yellow" />
                                        <span className="dot dot--green" />
                                        <span className="code-card__title">developer.js</span>
                                    </div>
                                    <pre className="code-card__body">
                                        <code>
                                            <span className="tok-kw">const</span> <span className="tok-var">developer</span> = {"{"}
                                            {"\n"}  <span className="tok-key">name</span>: <span className="tok-str">"Omar Hamsho"</span>,
                                            {"\n"}  <span className="tok-key">role</span>: <span className="tok-str">"Full-Stack Engineer"</span>,
                                            {"\n"}  <span className="tok-key">stack</span>: [<span className="tok-str">"React"</span>, <span className="tok-str">"Spring Boot"</span>],
                                            {"\n"}  <span className="tok-key">location</span>: <span className="tok-str">"Ludwigsburg, DE"</span>,
                                            {"\n"}  <span className="tok-key">available</span>: <span className="tok-bool">true</span>,
                                            {"\n"}{"}"};
                                        </code>
                                    </pre>
                                </div>
                            </div>
                            <div className="flip-card__face flip-card__face--back">
                                <div className="photo-card">
                                    <img src={omarPhoto} alt={personal.name} className="photo-card__img" />
                                    <div className="photo-card__caption">
                                        <span className="photo-card__name">{personal.name}</span>
                                        <span className="photo-card__role">{t.hero.roles[0]}</span>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>

                    <motion.div
                        className="floating-badge floating-badge--react"
                        animate={{ y: [0, 16, 0], rotate: [0, 6, 0] }}
                        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                    >
                        <FaReact />
                    </motion.div>
                    <motion.div
                        className="floating-badge floating-badge--java"
                        animate={{ y: [0, -18, 0], rotate: [0, -8, 0] }}
                        transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
                    >
                        <FaJava />
                    </motion.div>
                    <motion.div
                        className="floating-badge floating-badge--spring"
                        animate={{ y: [0, 14, 0] }}
                        transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut", delay: 1.1 }}
                    >
                        <SiSpringboot />
                    </motion.div>
                </motion.div>
            </div>

            <motion.a
                href="#about"
                className="hero__scroll-cue"
                onClick={(e) => {
                    e.preventDefault();
                    document.getElementById("about")?.scrollIntoView({ behavior: "smooth" });
                }}
                animate={{ y: [0, 8, 0] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                aria-label="Scroll to About section"
            >
                <span className="hero__scroll-cue-dot" />
            </motion.a>
        </section>
    );
}
